import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import dayjs from 'dayjs';

import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import { StyledFieldHeader, isFieldHeaderEmpty } from './FieldHeader';
import FieldInput from './FieldInput';

import FieldTypes from '../../../constants/FieldTypes';

import Tooltip from '../../misc/Tooltip';
import FieldModes from '../../../constants/FieldModes';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

const StyledDisplayText = styled.div`
  height: 100%;
  width: ${(props) => props.width};
  background-color: ${(props) => props.backgroundColor};
  font-size: ${(props) => props.fontSize}px !important;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  ${(props) => (props.inputGroupWrapMode && props.textOverflow !== 'ellipsis' ? 'overflow-wrap: break-word; white-space: pre-line;' : '')}
  border-radius: ${(props) => props.borderRadius}px;
  border: ${(props) => `${props.borderColor} ${props.borderWidth}px solid`};
  padding: ${(props) => props.padding}px;
  resize: none;
  transition: border 0.15s ease-in-out;

  &:focus, &:focus-visible {
    outline: unset;
  }

  &:hover {
    outline: unset;
  }
  ${(props) => (props.minHeight ? `min-height: ${props.minHeight}px;` : '')}
`;

const blockWrapStyles = (props) => `
  width: 100%;
  background-color: ${props.backgroundColor};
  font-size: ${props.fontSize}px !important;
  color: ${props.fontColor};
  font-style: ${props.fontStyle};
  font-weight: ${props.fontWeight};
  text-align: ${props.textAlign};
  text-transform: ${props.textTransform};
  text-decoration: ${props.textDecoration};
  border-radius: ${props.borderRadius}px;
  border: ${props.border};
  padding: ${props.padding}px;
  min-height: 25px;
  box-sizing: border-box;
`;

const StyledBlockWrapDisplay = styled.div`
  ${(props) => blockWrapStyles(props)}
  white-space: pre-line;
  overflow-wrap: break-word;
  cursor: pointer;

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

const StyledBlockWrapTextarea = styled.textarea`
  ${(props) => blockWrapStyles(props)}
  resize: none;
  overflow: hidden;
  transition: border 0.15s ease-in-out;

  &:focus, &:focus-visible {
    outline: unset;
    border: ${(props) => props.borderWidth}px solid ${(props) => props.primaryColor};
  }

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class BlockGenericField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    type: PropTypes.string,
    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    wrapperStyles: PropTypes.object,
    inputGroupWrapMode: PropTypes.bool,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,
    titleTextOverflow: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])

  }

  static defaultProps = {
    value: '',
    wrapperStyles: {}
  }

  constructor(props) {
    super(props);
    const { value } = props;
    this.state = { focused: false, changes: value };
    this.textareaRef = createRef();
    this.wrapDisplayHeight = null;
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { value } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });
  }

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {

      onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes } = this.state;

    const {

      wrapperStyles,

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      type,
      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      tabIndex,
      format,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,
      titleTextOverflow,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      placeholder,
      fieldMode,
      pageLayoutMode,
      fieldPresentationMode,
      decorators,
      onDecoratorClick,
      inputGroupWrapMode,

    } = this.props;


    const displayValue = getDisplayValue(type, changes, format);
    const border = `${borderWidth}px solid ${borderColor}`;
    const isWrapMode = inputGroupWrapMode && textOverflow !== 'ellipsis';

    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };

    let fieldNode;

    if (fieldMode === FieldModes.edit && isWrapMode) {

      const wrapTypography = {
        fontSize,
        fontColor,
        fontStyle,
        fontWeight,
        textAlign,
        textTransform,
        textDecoration,
        borderRadius,
        borderWidth,
        backgroundColor,
        padding,
        primaryColor,
        border,
      };

      if (focused && !disabled) {
        fieldNode = (
          <StyledBlockWrapTextarea
            data-testid={`${fieldPositionId}-text-field`}
            autoFocus
            ref={(e) => {
              this.textareaRef.current = e;
              if (!e) return;
              e.focus();
              e.selectionStart = e.selectionEnd = e.value.length;
            }}
            defaultValue={displayValue != null ? String(displayValue) : ''}
            style={this.wrapDisplayHeight ? { height: `${this.wrapDisplayHeight}px`, minHeight: `${this.wrapDisplayHeight}px` } : undefined}
            disabled={disabled}
            {...wrapTypography}
            onChange={(e) => {
              this.setState({ changes: e.target.value });
            }}
            onFocus={(e) => {
              if (!disabled && onFocus) {
                onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
              }
            }}
            onBlur={(e) => {
              const nextValue = e.target.value;
              if (focused) this.setState({ focused: false });
              if (!disabled && value !== nextValue) {
                onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);
              }
              if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
            }}
          />
        );
      } else {
        fieldNode = (
          <StyledBlockWrapDisplay
            data-testid={`${fieldPositionId}-text-field`}
            data-ig-wrap-display="true"
            ref={(e) => { if (e) this.wrapDisplayHeight = e.offsetHeight; }}
            disabled={disabled}
            {...wrapTypography}
            onClick={() => {
              if (!disabled) {
                this.setState({ focused: true });
              }
            }}
          >
            {displayValue != null ? String(displayValue) : ''}
          </StyledBlockWrapDisplay>
        );
      }

    } else if (fieldMode === FieldModes.edit) {

      fieldNode = (
        <FieldInput
          data-testid={`${fieldPositionId}-text-field`}
          tabIndex={tabIndex}
          value={displayValue}
          type={getFieldInputType(disabled, type, format)}
          placeholder={placeholder}
          minHeight={25}
          {...{
            disabled,
            focused,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            primaryColor,
            fieldId,
            fieldMode,
            pageLayoutMode,
            fieldPresentationMode,
          }}
          onChange={(e) => {
            this.setState({ changes: e.target.value });
          }}
          onFocus={(e) => {

            if (!focused) this.setState({ focused: true });

            if (!disabled && onFocus && !focused) {

              onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });

            }

          }}
          onBlur={(e) => {

            const nextValue = e.target.value;
            if (focused) this.setState({ focused: false });

            if (!disabled && value !== nextValue) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);
            }

            if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
          }}
        />
      );

    } else {

      fieldNode = (
        <StyledDisplayText
          disabled={disabled}
          focused={focused}
          fontSize={fontSize}
          fontColor={fontColor}
          fontStyle={fontStyle}
          fontWeight={fontWeight}
          textAlign={textAlign}
          textTransform={textTransform}
          textDecoration={textDecoration}
          textOverflow={textOverflow}
          inputGroupWrapMode={inputGroupWrapMode}
          borderColor={borderColor}
          borderRadius={borderRadius}
          borderWidth={borderWidth}
          backgroundColor={backgroundColor}
          padding={padding}
          primaryColor={primaryColor}
          minHeight={25}
        >
          {displayValue}
        </StyledDisplayText>
      );

    }

    return (
      <StyledWrapper {...{ margin }} style={wrapperStyles || {}}>
        <StyledInternalWrapper>
          <StyledFieldHeader $empty={isFieldHeaderEmpty({ title, titleDisplay, decorators, fieldMode })}>
            <FieldTitle
              {...{
                title,
                titleDisplay,
                titleFontSize,
                titleFontColor,
                titleTextAlign,
                titleFontStyle,
                titleFontWeight,
                titleTextTransform,
                titleTextDecoration,
                titleTextOverflow,
              }}
            />
            <FieldDecorators
              decorators={decorators}
              fieldMode={fieldMode}
              fieldId={fieldId}
              fieldIdentifier={fieldIdentifier}
              fieldPositionId={fieldPositionId}
              onDecoratorClick={onDecoratorClick}
            />
          </StyledFieldHeader>
          {getTooltip()}
          {fieldNode}
        </StyledInternalWrapper>
      </StyledWrapper>
    );
  }
}

export const getFieldInputType = (disabled, type, dateFormat) => {

  let inputType = 'text';

  if (type === FieldTypes.date) {

    if (dateFormat === 'MM/DD/YYY') {
      inputType = 'date';
    }
    else if (dateFormat === 'MM/DD/YYYY hh:mma') {
      inputType = 'datetime-local';
    }
    else if (dateFormat === 'hh:mma') {
      inputType = 'time';
    }
    else {
      inputType = 'date';
    }

  }
  else if (!disabled && type !== FieldTypes.date) {
    inputType = type;
  }

  return inputType;

};

export const getDisplayValue = (type, value, dateFormat) => {

  /**
   * <----------------------------NOTICE------------------------->
   * Text field component and functions inside this file are used in
   * many places.
   *
   * 0 is technically considered as a falsy value (!value) but, we have
   * value!==0 here so we could display 0 as the default value for xMin
   * and ymin in the chartFieldForm.
   *
   */

  if (!value && value !== 0) return '';

  if (type === 'date' && value) {

    let dayjsFormat = 'YYYY-MM-DD';

    if (dateFormat === 'MM/DD/YYYY') dayjsFormat = 'YYYY-MM-DD';

    else if (dateFormat === 'MM/DD/YYYY hh:mma') dayjsFormat = 'YYYY-MM-DDTHH:mm:ss';

    else if (dateFormat === 'hh:mma') dayjsFormat = 'HH:mm:ss';

    return dayjs(value).format(dayjsFormat);
  }
  else if (type === 'number' && !Number.isNaN(value)) {
    return value;
  }
  else if (value) {
    return value;
  }

};

export default BlockGenericField;
